import type { FormField, FormSchema, RequestPriority } from '@kfolks/types'
import { FormFieldType, RequestPriority as RequestPriorityEnum } from '@kfolks/types'
import { shouldSkipByCondition } from './conditional'

export interface FieldError {
	fieldId: string
	message: string
}

export interface ValidationResult {
	valid: boolean
	errors: FieldError[]
	/** Map keyed by fieldId for O(1) lookup in the per-field renderer. */
	errorsByField: Record<string, string>
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Client-side validation that mirrors `RequestService.validateSubmittedFormData`
 * (backend) so a USER sees the same errors before submit that the API would
 * raise. Conditional-skipped fields are exempt from required checks — the
 * backend uses the same rule.
 *
 * Errors are intentionally short and human-readable (no error codes); the
 * server is the source of truth for canonical errors and will re-raise on
 * submit if the client somehow drifted.
 */
export function validateFormData(formData: Record<string, unknown>, schema: FormSchema): ValidationResult {
	const errors: FieldError[] = []
	for (const field of schema.fields) {
		if (shouldSkipByCondition(field, formData)) continue
		const value = formData[field.id]
		const fieldErrors = validateField(field, value)
		for (const message of fieldErrors) errors.push({ fieldId: field.id, message })
	}
	const errorsByField: Record<string, string> = {}
	for (const e of errors) {
		// Surface the first error per field — keeps the UI uncluttered.
		if (!errorsByField[e.fieldId]) errorsByField[e.fieldId] = e.message
	}
	return { valid: errors.length === 0, errors, errorsByField }
}

function validateField(field: FormField, value: unknown): string[] {
	const errors: string[] = []
	const isEmpty = isEmptyValue(value)

	if (field.required && isEmpty) {
		errors.push(`${field.label} is required`)
		return errors // No further checks if the field is empty-and-required.
	}
	if (isEmpty) return errors // Optional + empty = no further checks.

	switch (field.type) {
		case FormFieldType.EMAIL:
			return validateEmail(field, value)
		case FormFieldType.NUMBER:
			return validateNumber(field, value)
		case FormFieldType.DATE:
			return validateDate(field, value)
		case FormFieldType.PRIORITY:
			return validatePriority(field, value)
		case FormFieldType.SELECT:
		case FormFieldType.RADIO:
			return validateEnum(field, value)
		case FormFieldType.MULTISELECT:
			return validateMultiselect(field, value)
		case FormFieldType.TEXT:
		case FormFieldType.TEXTAREA:
			return typeof value === 'string' ? validateString(field, value) : []
		case FormFieldType.PROVIDER_ACCESS_LIST:
			return validateProviderAccessList(field, value)
		default:
			// Other types (autocomplete family, file family, checkbox, pickers) have
			// no additional client-side checks — required + presence is enough.
			return []
	}
}

function validateProviderAccessList(field: FormField, value: unknown): string[] {
	if (!Array.isArray(value)) return [`${field.label} must be a list`]
	for (const entry of value) {
		if (!entry || typeof entry !== 'object') return [`${field.label} has an invalid entry`]
		const e = entry as Record<string, unknown>
		if (typeof e.provider !== 'string' || e.provider.length === 0) {
			return [`${field.label}: every entry needs a provider`]
		}
		if (typeof e.accessLevel !== 'string' || e.accessLevel.length === 0) {
			return [`${field.label}: every entry needs an access level`]
		}
	}
	return []
}

function isEmptyValue(value: unknown): boolean {
	return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
}

function validateEmail(field: FormField, value: unknown): string[] {
	return typeof value === 'string' && EMAIL_RE.test(value) ? [] : [`${field.label} must be a valid email address`]
}

function validateNumber(field: FormField, value: unknown): string[] {
	const errors: string[] = []
	const num = typeof value === 'number' ? value : Number(value)
	if (Number.isNaN(num)) return [`${field.label} must be a number`]
	if (field.validation?.min !== undefined && num < field.validation.min) {
		errors.push(`${field.label} must be ≥ ${field.validation.min}`)
	}
	if (field.validation?.max !== undefined && num > field.validation.max) {
		errors.push(`${field.label} must be ≤ ${field.validation.max}`)
	}
	return errors
}

function validateDate(field: FormField, value: unknown): string[] {
	return typeof value === 'string' && !Number.isNaN(new Date(value).getTime())
		? []
		: [`${field.label} must be a valid date`]
}

function validatePriority(field: FormField, value: unknown): string[] {
	return Object.values(RequestPriorityEnum).includes(value as RequestPriority)
		? []
		: [`${field.label} has an unknown priority value`]
}

function validateEnum(field: FormField, value: unknown): string[] {
	if (!field.options) return []
	return field.options.some((o) => o.value === value) ? [] : [`${field.label} value is not one of the allowed options`]
}

function validateMultiselect(field: FormField, value: unknown): string[] {
	if (!Array.isArray(value)) return [`${field.label} must be a list`]
	if (!field.options) return []
	const allowed = new Set(field.options.map((o) => o.value))
	const hasUnknown = value.some((v) => !allowed.has(v as string))
	return hasUnknown ? [`${field.label} contains a value not in the allowed options`] : []
}

function validateString(field: FormField, value: string): string[] {
	const errors: string[] = []
	if (field.validation?.min !== undefined && value.length < field.validation.min) {
		errors.push(`${field.label} must be at least ${field.validation.min} characters`)
	}
	if (field.validation?.max !== undefined && value.length > field.validation.max) {
		errors.push(`${field.label} must be at most ${field.validation.max} characters`)
	}
	if (field.validation?.pattern) {
		const re = new RegExp(field.validation.pattern)
		if (!re.test(value)) errors.push(field.validation.message ?? `${field.label} format is invalid`)
	}
	return errors
}
